/**
 * UA-2 — pure helpers for Service Agreement row actions (Final PRD §6 manual signing path; §52/§59). No API access.
 */
export const SIGNING_MODES = { MANUAL: 'MANUAL', ONLINE: 'ONLINE' };

/** Row-level signing actions for one agreement row. perms: { canCreate, canApprove }, flagEnabled: lifecycle flag.
 *  UA-4-3 (F7/X5): actions that exist for the status are always returned; when the caller lacks the permission they carry
 *  `disabled: true` + `reason` (disabled-with-reason, UO-2 rendering rule) instead of being hidden. */
const APPROVE_REASON = 'Requires the agreements.approve permission (approver persona — maker–checker)';
const CREATE_REASON = 'Requires the agreements.create permission';
export const NO_DOCUMENT_REASON = 'Generate the agreement document first (Details ▸ Documents)';   // UA-6-F20
export const signingRowActions = (agreement, perms, flagEnabled = true) => {
  if (!flagEnabled || !agreement) return [];
  const status = agreement.effective_status || agreement.status;
  const out = [];
  const gate = (ok, reason) => (ok ? {} : { disabled: true, reason });
  if (status === 'APPROVED_FOR_SIGNATURE') {
    // UA-6-F20 (option b): without a current frozen document the row offers "Generate agreement document" first and the
    // issue/send actions are disabled with that reason (the Day 5 generate route is the ONE generation architecture and is
    // not idempotent, so the UI never generates implicitly on issue). `has_current_document` undefined = legacy payload → unchanged.
    const noDoc = agreement.has_current_document === false;
    if (noDoc) out.push({ key: 'generate_document', label: 'Generate agreement document', hint: 'Creates the immutable frozen document (Details ▸ Documents) required before issuing for signature', ...gate(perms.canCreate, CREATE_REASON) });
    const docGate = noDoc ? { disabled: true, reason: NO_DOCUMENT_REASON } : gate(perms.canCreate, CREATE_REASON);
    out.push({ key: 'issue_manual', label: 'Download / print for manual signing', hint: 'Issues for signature (no e-mail), then downloads the frozen unsigned PDF (<agreement no>-Unsigned.pdf); opens it inline without the export permission', ...docGate });
    out.push({ key: 'send_online', label: 'Send for e-signature', hint: 'Online path — emails the frozen PDF to the address entered, or to the representative / participant email', ...docGate });
  }
  // F11 (RP-3): upload is gated by agreements.create; a different approver still reviews (maker–checker).
  if (['SENT_FOR_SIGNATURE', 'SENT'].includes(status)) out.push({ key: 'upload_signed', label: 'Upload signed copy', hint: 'Opens Details ▸ Documents at the current frozen document; an approver then reviews it', ...gate(perms.canCreate, CREATE_REASON) });
  if (status === 'SIGNATURE_REVIEW') out.push({ key: 'review_signed', label: 'Review signed copy', hint: 'An approver (agreements.approve) reviews the uploaded copy', ...gate(perms.canApprove, APPROVE_REASON) });
  return out;
};

/** UA-4-3: single hint line when the lifecycle flag is OFF for the organisation. */
export const FLAG_OFF_HINT = 'Agreement workflow is off for this organisation (Admin ▸ Organisation settings ▸ Feature flags); re-login after enabling';
export const workflowFlagHint = (flagEnabled) => (flagEnabled ? null : FLAG_OFF_HINT);

/** UA-4-2 (F6/X4): the frozen document is viewable at every status where one exists — never a catalogue of statuses. */
export const DOCUMENT_STATUSES = ['DRAFT', 'INTERNAL_REVIEW', 'CHANGES_REQUIRED', 'APPROVED_FOR_SIGNATURE', 'SENT', 'SENT_FOR_SIGNATURE', 'SIGNATURE_REVIEW', 'SIGNED', 'ACTIVE', 'EXPIRED', 'ENDED', 'CANCELLED'];
export const canViewDocument = (agreement) => DOCUMENT_STATUSES.includes(agreement?.effective_status || agreement?.status);
/** Live "Preview (unsaved render)" exists only while the agreement can still change (pre-signature). */
export const PREVIEW_STATUSES = ['DRAFT', 'INTERNAL_REVIEW', 'CHANGES_REQUIRED', 'APPROVED_FOR_SIGNATURE', 'SENT', 'SENT_FOR_SIGNATURE', 'SIGNATURE_REVIEW'];
export const canLivePreview = (agreement) => PREVIEW_STATUSES.includes(agreement?.effective_status || agreement?.status);

/** UA-4-3: approve-gated lifecycle buttons (Sign / Approve for signature / Activate) — same disabled-with-reason shape. */
export const approveGate = (canApprove) => (canApprove ? { disabled: false, reason: '' } : { disabled: true, reason: APPROVE_REASON });

/** UA-4-1 (F5/X3): logo changed after this document was generated? Compares the current branding sha with the frozen snapshot. */
export const logoChangedSinceGeneration = (doc, branding) => {
  if (!doc || !branding?.has_logo || !branding?.logo_sha256) return false;
  return (doc.branding_snapshot?.logo_sha256 || null) !== branding.logo_sha256;
};
export const LOGO_CHANGED_HINT = 'The organisation logo changed after this document was generated — generate a new document to include it (the earlier document is kept unchanged)';

/** Next-step hint per status (manual path, §6). */
export const nextStepHint = (agreement) => {
  const status = agreement?.effective_status || agreement?.status;
  return {
    DRAFT: 'Next: Submit for review → Approve for signature → Download / print for manual signing',
    INTERNAL_REVIEW: 'Next: an approver approves for signature',
    CHANGES_REQUIRED: 'Next: address the requested changes, then submit for review',
    APPROVED_FOR_SIGNATURE: agreement?.has_current_document === false
      ? 'Next: Generate agreement document (Details ▸ Documents) → Download / print for manual signing (or Send for e-signature) → upload the signed copy'
      : 'Next: Download / print for manual signing (or Send for e-signature) → upload the signed copy',
    SENT_FOR_SIGNATURE: 'Next: upload the externally signed copy (Details ▸ Documents) → an approver reviews it',
    SENT: 'Next: upload the externally signed copy (Details ▸ Documents) → an approver reviews it',
    SIGNATURE_REVIEW: 'Next: an approver reviews the uploaded copy → SIGNED (activation is a separate step)',
    SIGNED: 'Next: Activate (activation gates apply) — SIGNED is not ACTIVE',
  }[status] || null;
};

/** Awaiting-review chip for SIGNATURE_REVIEW rows: who uploaded, when (from the agreement or the pending document). */
export const awaitingReviewChip = (agreement, pendingDoc) => {
  const status = agreement?.effective_status || agreement?.status;
  if (status !== 'SIGNATURE_REVIEW') return null;
  const by = pendingDoc?.uploaded_by_name || pendingDoc?.uploaded_by_user_id || agreement?.signed_copy_uploaded_by || 'unknown user';
  const at = pendingDoc?.uploaded_at || agreement?.updated_at || null;
  return { label: 'Awaiting review', detail: `uploaded by ${by}${at ? ` · ${String(at).slice(0, 16).replace('T', ' ')}` : ''}` };
};

/** Why a Documents-panel action is disabled (shown instead of hiding the control). */
export const disabledReason = (action, status, perms) => {
  if (action === 'upload') {
    if (!perms.canCreate) return 'Requires the agreements.create permission';   // F11
    if (status === 'APPROVED_FOR_SIGNATURE') return 'Available after "Download / print for manual signing" or "Send for e-signature"';
    if (!['SENT_FOR_SIGNATURE', 'SENT'].includes(status)) return `Not available while the agreement is ${status}`;
  }
  if (action === 'sign') {
    if (!perms.canApprove) return 'Requires the agreements.approve permission';
    if (status === 'APPROVED_FOR_SIGNATURE') return 'Available after "Download / print for manual signing" or "Send for e-signature"';
    if (!['SENT_FOR_SIGNATURE', 'SENT'].includes(status)) return `Not available while the agreement is ${status}`;
  }
  if (action === 'review') {
    if (!perms.canApprove) return 'Requires the agreements.approve permission';
    if (status !== 'SIGNATURE_REVIEW') return 'No signed copy is awaiting review';
  }
  return null;
};

/** UA-6-F12 (rule B — Day 3 gate): why Save Draft is unavailable, listing the readiness blockers; null when it can be saved. */
export const saveDraftReason = (form, readiness) => {
  const missing = [];
  if (!form?.template_id) missing.push('select a template');
  if (!form?.start_date || !form?.end_date) missing.push('enter the start and end dates');
  if (missing.length) return missing.join('; ');
  if (readiness?.draft_allowed === false) {
    const blockers = (readiness.checks || []).filter((c) => c.state === 'BLOCK').map((c) => `${c.label}: ${c.detail}`);
    return blockers.length ? blockers.join(' · ') : 'the participant profile is incomplete (legal name, date of birth and 9-digit NDIS number are required before a draft can be saved)';
  }
  return null;
};

// ── UA-8 F30-B: controlled recovery of a signed-but-never-activated version ─────────────────────────────────────────
export const REGENERATION_ACTION = 'return_to_draft_for_regeneration';
export const REGENERATION_STATES = ['SIGNED', 'SIGNATURE_REVIEW'];
const REGEN_REASON_PERM = 'Requires the agreements.approve permission (Admin / Manager) — ask an approver to return this signed version to draft.';
const REGEN_REASON_STATE = (s) => (s === 'ACTIVE' ? 'An active agreement is changed through a Variation, never returned to draft.' : `Only a signed version that was never activated can be returned to draft (current ${s}).`);

/** Visible on SIGNED / SIGNATURE_REVIEW rows; disabled-with-reason for users without agreements.approve. Never offered on ACTIVE. */
export const regenerationGate = (agreement, canApprove) => {
  const s = agreement?.effective_status || agreement?.status;
  if (!REGENERATION_STATES.includes(s)) return { visible: false, disabled: true, reason: REGEN_REASON_STATE(s) };
  if (!canApprove) return { visible: true, disabled: true, reason: REGEN_REASON_PERM };
  return { visible: true, disabled: false, reason: '' };
};

/** cancel_draft is a destructive exit (TERMINATED): shown disabled-with-reason instead of hidden.
 *  Not tied to the extended-lifecycle flag — the generic /transition cancel_draft is available either way. */
export const cancelDraftGate = (agreement, canCreate) => {
  const s = agreement?.effective_status || agreement?.status;
  const states = ['DRAFT', 'SENT', 'SENT_FOR_SIGNATURE', 'SIGNATURE_REVIEW', 'SIGNED', 'INTERNAL_REVIEW', 'CHANGES_REQUIRED', 'APPROVED_FOR_SIGNATURE'];
  if (!states.includes(s)) return { visible: false, disabled: true, reason: '' };
  if (!canCreate) return { visible: true, disabled: true, reason: 'Requires the agreements.create permission.' };
  return { visible: true, disabled: false, reason: '' };
};

const MISMATCH_LINE_TEXT = {
  RATE_TABLE_DATA_MISSING: 'This line was signed as a rate table with no rows — it can never be activated as signed.',
  RATE_BASIS_CHANGED: 'The pricing basis of this line differs from the signed document.',
  RATE_CHANGED: 'The agreed rate of this line differs from the signed document.',
  NO_SIGNED_RATE: 'No signed rate is recorded for this line.',
  SUPPORT_ITEM_CHANGED: 'The NDIS support item of this line differs from the signed document.',
};

/** Activation refusal → the lines that failed, in user language (code kept as a small reference). */
export const activationMismatchLines = (err) => {
  const d = err?.response?.data?.detail || err?.response?.data?.error || err?.response?.data || {};
  if ((d.code || '') !== 'RATE_AUTHORITY_MISMATCH') return null;
  return (d.details?.lines || []).map((l) => ({ code: l.code, service_name: l.service_name || l.agreement_line_id || 'line', detail: l.detail || '',
    text: MISMATCH_LINE_TEXT[l.code] || 'This line no longer matches the signed document.' }));
};
